<script setup lang="ts">
const router = useRouter()
const { items, totalItems, isEmpty, updateQuantity, removeItem, clearCart } = useQuoteCart()

const loading = ref(false)
const error = ref<string | null>(null)
const showClearConfirm = ref(false)

// Handle form submission
const handleSubmit = async (customerData: {
  name: string
  email: string
  phone: string
  company: string
  notes: string
}) => {
  if (isEmpty.value) return

  loading.value = true
  error.value = null

  try {
    const response = await $fetch('/api/quotes', {
      method: 'POST',
      body: {
        customer: customerData,
        items: items.value.map(item => ({
          productId: item.productId,
          quantity: item.quantity
        }))
      }
    })

    // Clear cart and redirect to confirmation
    clearCart()
    await router.push(`/cotizacion/${response.quoteNumber}`)
  } catch (err: any) {
    console.error('Error creating quote:', err)

    // More specific error messages
    if (err.statusCode === 400) {
      error.value = 'Datos invalidos. Por favor revise el formulario.'
    } else if (err.statusCode === 500) {
      error.value = 'Error del servidor. Por favor intente mas tarde.'
    } else {
      error.value = err.data?.message || 'Error al crear la cotizacion. Por favor intente nuevamente.'
    }

    // Scroll to top smoothly on client only
    if (import.meta.client) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  } finally {
    loading.value = false
  }
}

// Handle quantity update
const handleUpdateQuantity = (productId: string, quantity: number) => {
  updateQuantity(productId, quantity)
}

// Handle remove
const handleRemove = (productId: string) => {
  removeItem(productId)
}

// Handle clear cart with confirmation
const handleClearCart = () => {
  showClearConfirm.value = true
}

const confirmClearCart = () => {
  clearCart()
  showClearConfirm.value = false
}

// Enhanced SEO
useSeoMeta({
  title: 'Mi Cotizacion - Granados Pinturas Industriales',
  description: 'Complete su solicitud de cotizacion de pinturas industriales.',
  robots: 'noindex, nofollow' // Don't index checkout pages
})

useHead({
  link: [
    { rel: 'canonical', href: 'https://granadospinturas.com/cotizacion' }
  ]
})
</script>

<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <UContainer class="py-6 md:py-10">
      <!-- Progress Steps Header - Enhanced -->
      <div class="mb-10 md:mb-16">
        <div class="flex items-center justify-center gap-2 md:gap-4 mb-8">
          <!-- Step 1 -->
          <div class="flex items-center">
            <div class="flex flex-col items-center">
              <div class="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 text-white flex items-center justify-center font-black text-xl md:text-2xl shadow-xl ring-4 ring-primary-100 dark:ring-primary-900/30">
                1
              </div>
              <p class="text-xs md:text-sm font-bold text-gray-900 dark:text-white mt-3 hidden sm:block high-contrast">
                Productos
              </p>
            </div>
          </div>

          <!-- Connector -->
          <div class="w-16 md:w-32 h-2 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full shadow-sm" />

          <!-- Step 2 -->
          <div class="flex items-center">
            <div class="flex flex-col items-center">
              <div class="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 text-white flex items-center justify-center font-black text-xl md:text-2xl shadow-xl ring-4 ring-primary-100 dark:ring-primary-900/30">
                2
              </div>
              <p class="text-xs md:text-sm font-bold text-gray-900 dark:text-white mt-3 hidden sm:block high-contrast">
                Datos
              </p>
            </div>
          </div>

          <!-- Connector -->
          <div class="w-16 md:w-32 h-2 bg-gray-200 dark:bg-gray-700 rounded-full" />

          <!-- Step 3 -->
          <div class="flex items-center">
            <div class="flex flex-col items-center">
              <div class="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 flex items-center justify-center font-black text-xl md:text-2xl">
                3
              </div>
              <p class="text-xs md:text-sm font-bold text-gray-500 dark:text-gray-400 mt-3 hidden sm:block">
                Confirmacion
              </p>
            </div>
          </div>
        </div>

        <!-- Page Title - Enhanced -->
        <div class="text-center">
          <h1 class="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 dark:text-white mb-4 high-contrast">
            Su Solicitud de Cotizacion
          </h1>
          <p class="text-base md:text-lg font-semibold text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Revise los productos y complete sus datos para recibir precios oficiales
          </p>
        </div>
      </div>

      <!-- Error Alert - Prominent -->
      <UAlert
        v-if="error"
        color="error"
        icon="i-heroicons-exclamation-triangle"
        class="mb-6 shadow-lg"
        variant="solid"
      >
        <template #title>
          Error al enviar cotizacion
        </template>
        <template #description>
          {{ error }}
        </template>
        <template #actions>
          <UButton
            color="error"
            variant="ghost"
            @click="error = null"
          >
            Cerrar
          </UButton>
        </template>
      </UAlert>

      <!-- Cart Content - Wrapped in ClientOnly to prevent hydration mismatch -->
      <ClientOnly>
        <!-- Empty State - Enhanced -->
        <div v-if="isEmpty" class="text-center py-16 md:py-24 empty-state">
          <div class="max-w-lg mx-auto">
            <div class="w-40 h-40 mx-auto mb-8 bg-gradient-to-br from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-800/50 rounded-3xl flex items-center justify-center shadow-xl border-2 border-gray-200 dark:border-gray-700">
              <UIcon name="i-heroicons-clipboard-document-list" class="w-20 h-20 text-gray-400 dark:text-gray-500" />
            </div>
            <h2 class="text-3xl md:text-4xl font-black text-gray-900 dark:text-white mb-4 high-contrast">
              Su lista esta vacia
            </h2>
            <p class="text-gray-600 dark:text-gray-400 mb-10 text-lg font-semibold">
              Agregue productos del catalogo para solicitar una cotizacion personalizada
            </p>
            <UButton to="/" color="primary" size="xl" class="shadow-xl hover:shadow-2xl btn-primary-enhanced touch-target font-black text-base">
              <UIcon name="i-heroicons-squares-plus-solid" class="w-6 h-6 mr-2" />
              Explorar Catalogo
            </UButton>
          </div>
        </div>

        <!-- Cart Content -->
        <div v-else class="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
        <!-- Cart Items - Larger on desktop -->
        <div class="lg:col-span-2 space-y-6">
          <!-- Products Card - Enhanced -->
          <UCard class="shadow-xl border-2 border-gray-200 dark:border-gray-700">
            <template #header>
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-4">
                  <div class="w-12 h-12 bg-gradient-to-br from-primary-100 to-primary-50 dark:from-primary-900/20 dark:to-primary-900/10 rounded-xl flex items-center justify-center shadow-sm border-2 border-primary-200 dark:border-primary-800">
                    <UIcon name="i-heroicons-shopping-cart-solid" class="w-6 h-6 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <h2 class="font-black text-xl text-gray-900 dark:text-white high-contrast">
                      Productos Seleccionados
                    </h2>
                    <p class="text-sm font-semibold text-gray-500 dark:text-gray-400">
                      {{ totalItems }} {{ totalItems === 1 ? 'producto' : 'productos' }}
                    </p>
                  </div>
                </div>
                <UButton
                  variant="soft"
                  color="error"
                  size="lg"
                  @click="handleClearCart"
                  :disabled="loading"
                  class="touch-target font-bold interactive-scale"
                >
                  <UIcon name="i-heroicons-trash-solid" class="w-4 h-4 mr-2" />
                  Vaciar
                </UButton>
              </div>
            </template>

            <div class="divide-y divide-gray-100 dark:divide-gray-800">
              <QuoteCartItem
                v-for="item in items"
                :key="item.productId"
                :item="item"
                @update-quantity="handleUpdateQuantity"
                @remove="handleRemove"
              />
            </div>

            <template #footer>
              <div class="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
                <div class="flex items-center gap-2">
                  <UIcon name="i-heroicons-information-circle-solid" class="w-5 h-5 text-blue-500" />
                  <span class="text-sm font-semibold text-gray-600 dark:text-gray-400">Los precios se calcularan en la cotizacion</span>
                </div>
                <NuxtLink to="/" class="group inline-flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-bold transition-colors">
                  <UIcon name="i-heroicons-plus-circle-solid" class="w-5 h-5 transition-transform group-hover:scale-110" />
                  Agregar mas productos
                </NuxtLink>
              </div>
            </template>
          </UCard>

          <!-- Help Card - Mobile only -->
          <UCard class="lg:hidden bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
            <div class="flex items-start gap-3">
              <UIcon name="i-heroicons-information-circle" class="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div class="text-sm">
                <p class="font-medium text-blue-900 dark:text-blue-100 mb-2">
                  Necesita ayuda?
                </p>
                <p class="text-blue-700 dark:text-blue-300 mb-3">
                  Nuestro equipo esta disponible para asesorarlo
                </p>
                <div class="flex flex-col gap-2">
                  <a href="tel:+525582960319" class="text-blue-600 dark:text-blue-400 hover:underline font-medium">
                    <UIcon name="i-heroicons-phone" class="w-4 h-4 inline mr-1" />
                    55 8296 0319
                  </a>
                  <a href="https://wa.me/525582960319" target="_blank" class="text-blue-600 dark:text-blue-400 hover:underline font-medium">
                    <UIcon name="i-heroicons-chat-bubble-left-right" class="w-4 h-4 inline mr-1" />
                    WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </UCard>
        </div>

        <!-- Contact Form Sidebar - Enhanced -->
        <div class="lg:sticky lg:top-6 lg:self-start">
          <UCard class="shadow-2xl border-2 border-gray-200 dark:border-gray-700">
            <template #header>
              <div class="flex items-center gap-4">
                <div class="w-12 h-12 bg-gradient-to-br from-primary-100 to-primary-50 dark:from-primary-900/20 dark:to-primary-900/10 rounded-xl flex items-center justify-center shadow-sm border-2 border-primary-200 dark:border-primary-800">
                  <UIcon name="i-heroicons-user-circle-solid" class="w-6 h-6 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <h2 class="font-black text-xl text-gray-900 dark:text-white high-contrast">
                    Datos de Contacto
                  </h2>
                  <p class="text-sm font-semibold text-gray-500 dark:text-gray-400">
                    Solo 3 campos requeridos
                  </p>
                </div>
              </div>
            </template>

            <QuoteContactForm
              v-model:loading="loading"
              @submit="handleSubmit"
            />
          </UCard>

          <!-- Help Card - Desktop only - Enhanced -->
          <UCard class="hidden lg:block mt-6 bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-900/20 dark:to-blue-900/10 border-2 border-blue-200 dark:border-blue-800 shadow-lg">
            <div class="flex items-start gap-4">
              <div class="flex-shrink-0 w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                <UIcon name="i-heroicons-information-circle-solid" class="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p class="font-black text-base text-blue-900 dark:text-blue-100 mb-2">
                  Necesita ayuda?
                </p>
                <p class="text-sm font-semibold text-blue-700 dark:text-blue-300 mb-4">
                  Nuestro equipo esta disponible para asesorarlo
                </p>
                <div class="space-y-3">
                  <a href="tel:+525582960319" class="group flex items-center gap-2 p-2.5 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors">
                    <UIcon name="i-heroicons-phone-solid" class="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    <span class="text-sm font-bold text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300">
                      55 8296 0319
                    </span>
                  </a>
                  <a href="https://wa.me/525582960319" target="_blank" class="group flex items-center gap-2 p-2.5 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors">
                    <UIcon name="i-heroicons-chat-bubble-left-right-solid" class="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    <span class="text-sm font-bold text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300">
                      WhatsApp
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </UCard>
        </div>
      </div>

        <template #fallback>
          <!-- Loading skeleton while cart state loads -->
          <div class="text-center py-16 md:py-24">
            <div class="max-w-lg mx-auto">
              <div class="w-40 h-40 mx-auto mb-8 bg-gray-200 dark:bg-gray-700 rounded-3xl animate-pulse" />
              <div class="h-8 bg-gray-200 dark:bg-gray-700 rounded w-48 mx-auto mb-4 animate-pulse" />
              <div class="h-6 bg-gray-200 dark:bg-gray-700 rounded w-64 mx-auto animate-pulse" />
            </div>
          </div>
        </template>
      </ClientOnly>

      <!-- Clear Confirmation Modal -->
      <UModal v-model="showClearConfirm">
        <UCard>
          <template #header>
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 bg-error-100 dark:bg-error-900/20 rounded-full flex items-center justify-center">
                <UIcon name="i-heroicons-exclamation-triangle" class="w-5 h-5 text-error-600 dark:text-error-400" />
              </div>
              <h3 class="text-lg font-semibold">Vaciar lista de cotizacion?</h3>
            </div>
          </template>

          <p class="text-gray-600 dark:text-gray-400 mb-6">
            Se eliminaran todos los productos de su lista. Esta accion no se puede deshacer.
          </p>

          <div class="flex gap-3 justify-end">
            <UButton
              color="neutral"
              variant="outline"
              @click="showClearConfirm = false"
            >
              Cancelar
            </UButton>
            <UButton
              color="error"
              @click="confirmClearCart"
            >
              Si, vaciar lista
            </UButton>
          </div>
        </UCard>
      </UModal>
    </UContainer>
  </div>
</template>
